import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getUserScanHistory, UserScanHistory } from '../../services/api/securityApi';

const UserScanHistoryPage: React.FC = () => {
  const [scans, setScans] = useState<UserScanHistory[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minThreats, setMinThreats] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedScan, setSelectedScan] = useState<UserScanHistory | null>(null);

  useEffect(() => {
    fetchScans();
  }, [page, rowsPerPage, minThreats]);

  const fetchScans = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (minThreats) {
        params.minThreats = parseInt(minThreats);
      }
      const data = await getUserScanHistory(params);
      setScans(data.items);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.message || '데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetail = (scan: UserScanHistory) => {
    setSelectedScan(scan);
    setDetailOpen(true);
  };

  const getRiskColor = (threats: number) => {
    if (threats === 0) return 'success';
    if (threats <= 2) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">유저 스캔 내역</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchScans}
          disabled={loading}
        >
          새로고침
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="최소 위협 수"
            type="number"
            value={minThreats}
            onChange={(e) => setMinThreats(e.target.value)}
            size="small"
            sx={{ width: 150 }}
            placeholder="0"
          />
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>사용자</TableCell>
              <TableCell>스캔 유형</TableCell>
              <TableCell align="center">총 앱</TableCell>
              <TableCell align="center">안전</TableCell>
              <TableCell align="center">위험</TableCell>
              <TableCell>기기 정보</TableCell>
              <TableCell>스캔 일시</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : scans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              scans.map((scan) => (
                <TableRow key={scan.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {scan.user?.username || '알 수 없음'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {scan.user?.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={scan.scanType === 'full' ? '전체' : scan.scanType}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">{scan.totalApps}</TableCell>
                  <TableCell align="center">
                    <Chip label={scan.safeApps} size="small" color="success" />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={scan.threatsFound}
                      size="small"
                      color={getRiskColor(scan.threatsFound) as any}
                    />
                  </TableCell>
                  <TableCell>
                    {scan.deviceInfo ? (
                      <Typography variant="caption">
                        {scan.deviceInfo.brand} {scan.deviceInfo.model}
                        <br />
                        Android {scan.deviceInfo.systemVersion}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(scan.createdAt).toLocaleString('ko-KR')}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="상세보기">
                      <IconButton size="small" onClick={() => handleViewDetail(scan)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="페이지당 행 수:"
        />
      </TableContainer>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>스캔 상세 정보</DialogTitle>
        <DialogContent>
          {selectedScan && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">사용자</Typography>
                  <Typography>{selectedScan.user?.username} ({selectedScan.user?.email})</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">스캔 일시</Typography>
                  <Typography>{new Date(selectedScan.createdAt).toLocaleString('ko-KR')}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">스캔 소요 시간</Typography>
                  <Typography>{selectedScan.scanDuration}초</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">기기 정보</Typography>
                  <Typography>
                    {selectedScan.deviceInfo?.brand} {selectedScan.deviceInfo?.model} (Android {selectedScan.deviceInfo?.systemVersion})
                  </Typography>
                </Box>
              </Box>

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>스캔 결과 요약</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip label={`총 ${selectedScan.totalApps}개`} />
                <Chip label={`안전 ${selectedScan.safeApps}개`} color="success" />
                <Chip label={`낮음 ${selectedScan.lowRiskApps}개`} color="info" />
                <Chip label={`중간 ${selectedScan.mediumRiskApps}개`} color="warning" />
                <Chip label={`높음 ${selectedScan.highRiskApps}개`} color="error" />
              </Box>

              {selectedScan.threatDetails && selectedScan.threatDetails.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>위험도별 앱 목록</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>앱 이름</TableCell>
                          <TableCell>패키지명</TableCell>
                          <TableCell>위험도</TableCell>
                          <TableCell>위협</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedScan.threatDetails.map((threat, index) => (
                          <TableRow key={index}>
                            <TableCell>{threat.appName}</TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                {threat.packageName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={threat.riskLevel === 'high' ? '높음' : threat.riskLevel === 'medium' ? '중간' : '낮음'}
                                size="small"
                                color={threat.riskLevel === 'high' ? 'error' : threat.riskLevel === 'medium' ? 'warning' : 'info'}
                              />
                            </TableCell>
                            <TableCell>
                              {threat.threats.slice(0, 2).join(', ')}
                              {threat.threats.length > 2 && ` 외 ${threat.threats.length - 2}개`}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserScanHistoryPage;
